import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Eye, EyeOff, CheckCircle2, Info, ArrowUp, ArrowDown } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";

const MODES = [
  { key: "optional", label: "Optional", hint: "Visitors choose it" },
  { key: "preselected", label: "Pre-ticked", hint: "On by default, can be removed" },
  { key: "always", label: "Always included", hint: "Mandatory, can't be removed" },
];

const rule = (s) => ({ hidden: false, mode: "optional", listed: true, order: null, note: "", show_levels: false, requires_bookkeeping: false, requires_bookkeeping_fixed: false, no_onboarding: false, no_onboarding_fixed: false, ...(s.display || {}) });

// Choose what the public builder offers. Presentation only — every figure still
// comes from the pricing engine, and a mandatory service is priced on every quote.
export default function PackageServicesTab() {
  const [data, setData] = useState(null);
  const [rules, setRules] = useState({});
  const [bkId, setBkId] = useState("");
  const [maskedKeys, setMaskedKeys] = useState([]);
  const [hints, setHints] = useState({});
  const [labels, setLabels] = useState({});
  const [presets, setPresets] = useState({});
  const [helps, setHelps] = useState({});
  const [helpStats, setHelpStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    adminApi.packageBuilder.catalogue()
      .then((d) => {
        setData(d);
        setMaskedKeys(d.masked_help_keys || []);
        setHints(d.help_hints || {});
        setLabels(d.label_overrides || {});
        setPresets(d.default_answers || {});
        setHelps(d.question_help || {});
        adminApi.packageBuilder.helpStats().then(setHelpStats).catch(() => setHelpStats({ days: 90, total: 0, keys: [] }));
        const next = {};
        (d.services || []).forEach((s) => { next[s.service_id] = rule(s); });
        setRules(next);
      })
      .catch(() => setError("Couldn't load the service list from the pricing tool."));
    adminApi.packageBuilder.getConfig()
      .then((c) => setBkId(c.bookkeeping_service_id || ""))
      .catch(() => {});
  }, []);

  const set = (id, patch) => {
    setRules((r) => ({ ...r, [id]: { ...r[id], ...patch } }));
    setSaved(false);
  };

  const setHelp = (key, patch) => {
    setHelps((h) => ({ ...h, [key]: { enabled: true, type: "standard", ...h[key], ...patch } }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cfg = await adminApi.packageBuilder.getConfig();
      await adminApi.packageBuilder.saveConfig({ ...cfg, services: rules, bookkeeping_service_id: bkId, masked_help_keys: maskedKeys, help_hints: hints, label_overrides: labels, default_answers: presets, question_help: helps });
      setSaved(true);
    } catch {
      setError("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const bulk = (predicate, patch) => {
    setRules((r) => {
      const next = { ...r };
      (data.services || []).filter(predicate).forEach((s) => { next[s.service_id] = { ...next[s.service_id], ...patch }; });
      return next;
    });
    setSaved(false);
  };

  // One flat list in the order visitors see it, so reordering is obvious.
  const ordered = useMemo(() => {
    const items = [...(data?.services || [])];
    items.sort((a, b) => {
      const ra = rules[a.service_id] || rule(a);
      const rb = rules[b.service_id] || rule(b);
      const oa = ra.order ?? (data.services.indexOf(a) * 10);
      const ob = rb.order ?? (data.services.indexOf(b) * 10);
      return oa - ob;
    });
    return items;
  }, [data, rules]);

  const move = (id, dir) => {
    const ids = ordered.map((s) => s.service_id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setRules((r) => {
      const next = { ...r };
      ids.forEach((sid, idx) => { next[sid] = { ...next[sid], order: idx * 10 }; });
      return next;
    });
    setSaved(false);
  };

  const counts = useMemo(() => {
    const vals = Object.values(rules);
    return {
      hidden: vals.filter((r) => r.hidden && r.mode === "optional").length,
      always: vals.filter((r) => r.mode === "always").length,
      offered: vals.filter((r) => !r.hidden && r.mode !== "always").length,
    };
  }, [rules]);

  const reworded = useMemo(
    () => new Set([...Object.keys(hints), ...Object.keys(labels)].filter((k) => hints[k] || labels[k])).size,
    [hints, labels],
  );
  const questionLabels = useMemo(() => {
    const out = {};
    (data?.services || []).forEach((s) => (s.questions || []).forEach((q) => {
      out[q.key] = labels[q.key] || q.label || q.key;
    }));
    return out;
  }, [data, labels]);
  const hiddenLines = useMemo(() => {
    const keys = new Set();
    (data?.services || []).forEach((s) => (s.questions || []).forEach((q) => {
      if (q.auto_masked || maskedKeys.includes(q.key)) keys.add(q.key);
    }));
    return keys.size;
  }, [data, maskedKeys]);

  if (error && !data) return <p className="text-sm text-orange-dark" data-testid="package-services-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-400">Loading the service list…</p>;

  return (
    <div data-testid="package-services-tab">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <h2 className="font-display font-bold text-navy">What the builder offers</h2>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          This changes what visitors see and what's included by default. It does <strong>not</strong> change your internal pricing
          tool, and it can't change a price — every figure still comes from the pricing engine. A mandatory service is sent to the
          engine on every quote, so the price shown already covers it.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button variant="outline" size="sm" className="rounded-full" data-testid="package-bulk-hide-oneoff"
            onClick={() => bulk((s) => s.billing_frequency === "one_off" && s.display?.mode !== "always", { hidden: true, mode: "optional" })}>
            Hide all one-off items
          </Button>
          <Button variant="outline" size="sm" className="rounded-full" data-testid="package-bulk-show-all"
            onClick={() => bulk(() => true, { hidden: false })}>
            Show everything
          </Button>
          <Button variant="outline" size="sm" className="rounded-full" data-testid="package-bulk-reset"
            onClick={() => bulk(() => true, { hidden: false, mode: "optional", listed: true })}>
            Reset all to optional
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-4">
          {counts.offered} offered to visitors · {counts.always} always included · {counts.hidden} hidden ·
          pricing version {data.pricing_version || "unknown"}
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid="package-help-stats">
        <h2 className="font-display font-bold text-navy">Which help people open</h2>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          Every time a visitor taps the ⓘ beside a question, it's counted here — no names, no answers, just the question.
          What gets opened most is a list of what the question itself still fails to say.
        </p>
        {!helpStats ? (
          <p className="text-xs text-slate-400 mt-4">Loading…</p>
        ) : helpStats.keys.length === 0 ? (
          <p className="text-xs text-slate-400 mt-4">Nobody has opened any help yet. This fills up as visitors use the pricing tool.</p>
        ) : (
          <>
            <ul className="mt-4 space-y-1.5 max-w-2xl">
              {helpStats.keys.slice(0, 10).map((row) => (
                <li key={row.key} className="flex items-center gap-3" data-testid={`package-help-stat-${row.key}`}>
                  <span className="text-xs text-navy font-semibold flex-1 truncate">{questionLabels[row.key] || row.key}</span>
                  <span className="h-1.5 rounded-full bg-orange/70" style={{ width: `${Math.max(6, (row.opens / helpStats.keys[0].opens) * 160)}px` }} />
                  <span className="text-xs font-bold text-navy w-8 text-right">{row.opens}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-400 mt-3">
              {helpStats.total} opened in the last {helpStats.days} days across {helpStats.keys.length} question
              {helpStats.keys.length === 1 ? "" : "s"}.
            </p>
          </>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid="package-eligibility-panel">
        <h2 className="font-display font-bold text-navy">Services that require Bookkeeping</h2>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          Some services CASS only provides as part of joined-up finance support. Mark them below and visitors will see a small lock
          with a short explanation, and a one-click way to add Bookkeeping without losing anything they've entered. This is a
          <strong> policy setting, not a price</strong>: there is no minimum fee here and there never will be — your Proposal Tool
          applies its own Minimum Monthly Fee to Bookkeeping, so the website only has to insist Bookkeeping is chosen.
        </p>
        <label className="block mt-4 max-w-md">
          <span className="text-xs font-semibold text-navy">Which service counts as Bookkeeping?</span>
          <select
            value={bkId}
            onChange={(e) => { setBkId(e.target.value); setSaved(false); }}
            data-testid="package-bookkeeping-select"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-orange/40"
          >
            <option value="">No requirement — nothing is locked</option>
            {(data.services || []).map((s) => (
              <option key={s.service_id} value={s.service_id}>{s.name}</option>
            ))}
          </select>
        </label>
        {bkId && rules[bkId]?.mode === "always" && (
          <p className="text-xs text-teal mt-3 flex items-start gap-1.5">
            <Info size={13} className="shrink-0 mt-0.5" /> {data.services.find((s) => s.service_id === bkId)?.name} is always
            included, so every restricted service is permanently available and no lock is ever shown.
          </p>
        )}
        <p className="text-xs text-slate-400 mt-3">
          Tick <strong>Requires Bookkeeping</strong> on any service in the list below. Currently{" "}
          {Object.values(rules).filter((r) => r.requires_bookkeeping).length} marked.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid="package-help-masking">
        <h2 className="font-display font-bold text-navy">Question wording</h2>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          Titles and explanatory lines come from your Proposal Tool, where they're written for your team. Any line mentioning a
          discount, a percentage or a £ figure is hidden from visitors automatically; you can hide any other, retitle a question,
          and write your own hint underneath. The question is always asked and the answer always reaches your Proposal Tool.
        </p>
        <p className="text-[11px] text-slate-400 mt-3">
          {reworded} question{reworded === 1 ? "" : "s"} reworded · {hiddenLines} explanatory line{hiddenLines === 1 ? "" : "s"} hidden
        </p>
        <div className="mt-4 space-y-2">
          {(data.services || []).map((s) => {
            const qs = s.questions || [];
            if (qs.length === 0) return null;
            const changed = qs.filter((q) => hints[q.key] || labels[q.key]).length;
            const hidden = qs.filter((q) => q.auto_masked || maskedKeys.includes(q.key)).length;
            return (
              <details key={s.service_id} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`package-help-service-${s.service_id}`}>
                <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-navy hover:bg-slate-50 flex items-center justify-between gap-3">
                  <span>{s.name}</span>
                  <span className="text-[11px] font-semibold text-slate-400">
                    {qs.length} question{qs.length === 1 ? "" : "s"}
                    {changed > 0 && <span className="text-teal"> · {changed} reworded</span>}
                    {hidden > 0 && <span className="text-slate-500"> · {hidden} hidden</span>}
                  </span>
                </summary>
                <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                  {qs.map((h) => {
                    const off = h.auto_masked || maskedKeys.includes(h.key);
                    return (
                      <div
                        key={h.key}
                        className={`rounded-xl border p-4 ${off ? "border-slate-100 bg-slate-50" : "border-slate-200"}`}
                        data-testid={`package-help-${h.key}`}
                      >
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="min-w-[240px] flex-1">
                            <p className="text-sm font-bold text-navy">{labels[h.key] || h.label}</p>
                            {labels[h.key] && <p className="text-[11px] text-slate-400 mt-0.5">Their title: {h.label}</p>}
                            {h.help_text && (
                              <p className={`text-xs mt-1 leading-relaxed ${off ? "text-slate-400 line-through" : "text-slate-500"}`}>
                                “{h.help_text}”
                              </p>
                            )}
                          </div>
                          {h.help_text ? (
                            h.auto_masked ? (
                              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase bg-navy/5 text-navy px-2.5 py-1.5 rounded-full">
                                <EyeOff size={12} /> Hidden — explains a fee
                              </span>
                            ) : (
                              <button
                                onClick={() => {
                                  setMaskedKeys((k) => (k.includes(h.key) ? k.filter((x) => x !== h.key) : [...k, h.key]));
                                  setSaved(false);
                                }}
                                data-testid={`package-help-toggle-${h.key}`}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                                  off ? "bg-navy text-white border-navy" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                                }`}
                              >
                                {off ? <EyeOff size={13} /> : <Eye size={13} />} {off ? "Hidden" : "Shown"}
                              </button>
                            )
                          ) : (
                            <span className="text-[11px] text-slate-400">No line of their own</span>
                          )}
                        </div>
                        <div className="grid md:grid-cols-2 gap-3 mt-3">
                          <label className="block">
                            <span className="text-xs font-semibold text-navy">Title visitors see (optional)</span>
                            <input
                              type="text"
                              value={labels[h.key] || ""}
                              placeholder="Leave blank to use their title"
                              onChange={(e) => { setLabels((v) => ({ ...v, [h.key]: e.target.value })); setSaved(false); }}
                              data-testid={`package-help-label-${h.key}`}
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-orange/40"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs font-semibold text-navy">Hint underneath (optional)</span>
                            <input
                              type="text"
                              value={hints[h.key] || ""}
                              placeholder={off || !h.help_text ? "Nothing shown — write a plain-English hint" : "Leave blank to keep their line"}
                              onChange={(e) => { setHints((v) => ({ ...v, [h.key]: e.target.value })); setSaved(false); }}
                              data-testid={`package-help-hint-${h.key}`}
                              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-orange/40"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs font-semibold text-navy">Pre-selected answer (optional)</span>
                            {(h.options || []).length > 0 ? (
                              <select
                                value={presets[h.key] ?? ""}
                                onChange={(e) => { setPresets((v) => ({ ...v, [h.key]: e.target.value })); setSaved(false); }}
                                data-testid={`package-help-default-${h.key}`}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-orange/40"
                              >
                                <option value="">Nothing pre-selected</option>
                                {h.options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                              </select>
                            ) : (
                              <input
                                type="text"
                                value={presets[h.key] ?? ""}
                                placeholder="Leave blank for an empty field"
                                onChange={(e) => { setPresets((v) => ({ ...v, [h.key]: e.target.value })); setSaved(false); }}
                                data-testid={`package-help-default-${h.key}`}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-orange/40"
                              />
                            )}
                            <span className="block text-[11px] text-slate-400 mt-1">Visitors can still change it, and the price follows what they choose.</span>
                          </label>
                        </div>

                        {/* Contextual help behind the ⓘ */}
                        {(() => {
                          const hp = helps[h.key] || {};
                          const on = !!hp.enabled && (!!hp.text || (hp.items || []).length > 0);
                          const extended = hp.type === "extended" || (hp.items || []).length > 0;
                          return (
                            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <p className="text-xs font-bold text-navy">Contextual help (ⓘ beside the question)</p>
                                <button
                                  type="button"
                                  onClick={() => setHelp(h.key, { enabled: !hp.enabled })}
                                  data-testid={`package-help-ctx-toggle-${h.key}`}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                                    on ? "bg-teal text-white border-teal" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                                  }`}
                                >
                                  {on ? "Help on" : "Help off"}
                                </button>
                              </div>
                              <div className="grid md:grid-cols-3 gap-3 mt-3">
                                <label className="block">
                                  <span className="text-xs font-semibold text-navy">Heading</span>
                                  <input
                                    type="text"
                                    value={hp.heading || ""}
                                    placeholder="Why do we ask?"
                                    onChange={(e) => setHelp(h.key, { heading: e.target.value })}
                                    data-testid={`package-help-ctx-heading-${h.key}`}
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-orange/40"
                                  />
                                </label>
                                <label className="block">
                                  <span className="text-xs font-semibold text-navy">Format</span>
                                  <select
                                    value={extended ? "extended" : "standard"}
                                    onChange={(e) => setHelp(h.key, { type: e.target.value })}
                                    data-testid={`package-help-ctx-type-${h.key}`}
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-orange/40"
                                  >
                                    <option value="standard">Standard — a short explanation</option>
                                    <option value="extended">Extended — explain each option</option>
                                  </select>
                                </label>
                                <label className="block md:col-span-3">
                                  <span className="text-xs font-semibold text-navy">Help text</span>
                                  <textarea
                                    rows={2}
                                    value={hp.text || ""}
                                    placeholder="Plain English, reassuring, no jargon."
                                    onChange={(e) => setHelp(h.key, { text: e.target.value })}
                                    data-testid={`package-help-ctx-text-${h.key}`}
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-orange/40"
                                  />
                                </label>
                                {extended && (
                                  <>
                                    <label className="block md:col-span-3">
                                      <span className="text-xs font-semibold text-navy">One option per line — <em>Label | what it means</em></span>
                                      <textarea
                                        rows={5}
                                        value={(hp.items || []).map((it) => `${it.label} | ${it.text}`).join("\n")}
                                        onChange={(e) => setHelp(h.key, {
                                          items: e.target.value.split("\n").filter((l) => l.trim()).map((l) => {
                                            const [label, ...rest] = l.split("|");
                                            return { label: label.trim(), text: rest.join("|").trim() };
                                          }),
                                        })}
                                        data-testid={`package-help-ctx-items-${h.key}`}
                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-orange/40"
                                      />
                                    </label>
                                    <label className="block md:col-span-3">
                                      <span className="text-xs font-semibold text-navy">Closing line (optional)</span>
                                      <input
                                        type="text"
                                        value={hp.footer || ""}
                                        onChange={(e) => setHelp(h.key, { footer: e.target.value })}
                                        data-testid={`package-help-ctx-footer-${h.key}`}
                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-orange/40"
                                      />
                                    </label>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </details>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-4">
        <h3 className="font-display font-bold text-navy text-sm uppercase tracking-wide">Order shown to visitors</h3>
        <p className="text-xs text-slate-500 mt-1 mb-4">
          Use the arrows to reorder. Visitors see them grouped by category, in this order, so moving a service also moves its
          category.
        </p>
        <div className="space-y-3">
          {ordered.map((s, i) => {
            const r = rules[s.service_id] || rule(s);
            const gone = r.hidden && r.mode === "optional";
            return (
              <div key={s.service_id} className={`rounded-xl border p-4 transition-colors ${gone ? "border-slate-100 bg-slate-50/60" : "border-slate-200"}`} data-testid={`package-svc-${s.service_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 min-w-[240px]">
                    <div className="flex flex-col gap-0.5 pt-0.5">
                      <button onClick={() => move(s.service_id, -1)} disabled={i === 0} className="text-slate-400 hover:text-navy disabled:opacity-25" title="Move up" data-testid={`package-svc-up-${s.service_id}`}><ArrowUp size={14} /></button>
                      <button onClick={() => move(s.service_id, 1)} disabled={i === ordered.length - 1} className="text-slate-400 hover:text-navy disabled:opacity-25" title="Move down" data-testid={`package-svc-down-${s.service_id}`}><ArrowDown size={14} /></button>
                    </div>
                    <div>
                      <p className={`text-sm font-bold ${gone ? "text-slate-400" : "text-navy"}`}>
                        {s.name}
                        {s.billing_frequency === "one_off" && (
                          <span className="ml-2 text-[10px] font-bold uppercase bg-navy/5 text-navy px-1.5 py-0.5 rounded align-middle">One-off</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {s.category} · {s.question_count} question{s.question_count === 1 ? "" : "s"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => set(s.service_id, { hidden: !r.hidden })}
                      data-testid={`package-svc-hide-${s.service_id}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${r.hidden ? "bg-navy text-white border-navy" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
                    >
                      {r.hidden ? <EyeOff size={13} /> : <Eye size={13} />} {r.hidden ? "Hidden" : "Shown"}
                    </button>
                    <div className="flex rounded-full border border-slate-200 overflow-hidden">
                      {MODES.map((m) => (
                        <button
                          key={m.key}
                          title={m.hint}
                          onClick={() => set(s.service_id, { mode: m.key })}
                          data-testid={`package-svc-mode-${m.key}-${s.service_id}`}
                          className={`px-3 py-1.5 text-xs font-bold transition-colors ${r.mode === m.key ? "bg-orange text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <label className="block mt-3">
                  <span className="text-xs font-semibold text-navy">What visitors read about it</span>
                  <textarea
                    rows={2}
                    value={r.note || ""}
                    placeholder={s.description && s.description !== s.name ? s.description : "No description comes from the pricing tool — worth writing one."}
                    onChange={(e) => set(s.service_id, { note: e.target.value })}
                    data-testid={`package-svc-note-${s.service_id}`}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-orange/40"
                  />
                  <span className="block text-[11px] text-slate-400 mt-1">
                    Leave blank to use the pricing tool's own wording. Useful for explaining exactly what a mandatory fee covers.
                  </span>
                </label>

                {(s.questions || []).some((q) => ((helps[q.key] || {}).items || []).length > 0) && (
                  <label className="mt-3 flex items-start gap-2 cursor-pointer" data-testid={`package-svc-show-levels-${s.service_id}`}>
                    <input
                      type="checkbox"
                      checked={!!r.show_levels}
                      onChange={(e) => set(s.service_id, { show_levels: e.target.checked })}
                      className="mt-0.5 accent-orange"
                    />
                    <span className="text-xs text-slate-600 leading-relaxed">
                      <strong className="text-navy">Show the levels on the card.</strong> Lists each option and what it means on
                      the service card itself, so visitors can compare before they choose it.
                    </span>
                  </label>
                )}

                {bkId && s.service_id !== bkId && r.mode !== "always" && (
                  <label className={`mt-3 flex items-start gap-2 ${r.requires_bookkeeping_fixed ? "" : "cursor-pointer"}`} data-testid={`package-svc-requires-bk-${s.service_id}`}>
                    <input
                      type="checkbox"
                      checked={!!r.requires_bookkeeping}
                      disabled={!!r.requires_bookkeeping_fixed}
                      onChange={(e) => set(s.service_id, { requires_bookkeeping: e.target.checked })}
                      className="mt-0.5 accent-orange"
                    />
                    <span className="text-xs text-slate-600 leading-relaxed">
                      Requires Bookkeeping — visitors can't pick this on its own, and are offered Bookkeeping in one click.
                      {r.requires_bookkeeping_fixed && <span className="text-slate-400"> Always on for this service.</span>}
                    </span>
                  </label>
                )}

                {r.mode !== "always" && (
                  <label className={`mt-3 flex items-start gap-2 ${r.no_onboarding_fixed ? "" : "cursor-pointer"}`} data-testid={`package-svc-no-onboarding-${s.service_id}`}>
                    <input
                      type="checkbox"
                      checked={!!r.no_onboarding}
                      disabled={!!r.no_onboarding_fixed}
                      onChange={(e) => set(s.service_id, { no_onboarding: e.target.checked })}
                      className="mt-0.5 accent-orange"
                    />
                    <span className="text-xs text-slate-600 leading-relaxed">
                      No onboarding fee — if this is the only thing a visitor chooses, the New Client Onboarding Fee is left out of
                      the price, because the work doesn't take them on as a client. Pick anything else alongside it and the fee
                      applies as normal.
                      {r.no_onboarding_fixed && <span className="text-slate-400"> Always on for this service.</span>}
                    </span>
                  </label>
                )}

                {r.mode === "always" && (                  <label className="mt-3 flex items-start gap-2 cursor-pointer" data-testid={`package-svc-listed-${s.service_id}`}>
                    <input
                      type="checkbox"
                      checked={r.listed !== false}
                      onChange={(e) => set(s.service_id, { listed: e.target.checked })}
                      className="mt-0.5 accent-orange"
                    />
                    <span className="text-xs text-slate-600 leading-relaxed">
                      Tell visitors it's included — shows under "Included as standard" so the fee isn't a surprise.
                      {r.listed === false && <span className="block text-orange-dark font-semibold mt-1">Currently invisible: it's in the price with nothing on screen to explain it.</span>}
                    </span>
                  </label>
                )}
                {r.hidden && r.mode === "preselected" && (
                  <p className="text-xs text-slate-500 mt-3 flex items-start gap-1.5">
                    <Info size={13} className="shrink-0 mt-0.5" /> Hidden and pre-ticked can't be un-ticked by a visitor, so this
                    behaves as Always included.
                  </p>
                )}
                {gone && <p className="text-xs text-slate-400 mt-3">Not offered and not priced — visitors won't see it at all.</p>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="sticky bottom-4 flex items-center gap-3 pb-4">
        <Button onClick={save} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full shadow-lg" data-testid="package-services-save">
          {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} Save what's shown
        </Button>
        {saved && <span className="text-sm text-teal flex items-center gap-1.5"><CheckCircle2 size={15} /> Saved</span>}
        {error && <span className="text-sm text-orange-dark">{error}</span>}
      </div>
    </div>
  );
}
